// A new business's first steps at the top of the conversation with Ogma (Craig, 31 Oct 2026; lib/firstSteps.ts): Ogma
// greets the business and walks through what is left, one step at a time. The step to do now is open, with its buttons:
// tap answers for the sector, ask Ogma (it answers and acts in the chat), or open the page beside the chat. The list
// shows what is done and what comes next. It goes once every step is done.
import { useState } from "react";
import { Check, ChevronRight } from "lucide-react";
import { useCrm, useWorkspace } from "../../lib/session";
import { nextStep, SECTOR_ANSWERS, skipContacts, type FirstStep, type FirstStepMove } from "../../lib/firstSteps";
import { Notice } from "../ui";

interface Props {
  steps: FirstStep[];
  onAsk: (text: string) => void;
  onOpenPage: (path: string, title: string) => void;
  /** Read the steps again (after the sector is set or the contacts skipped). */
  onChanged: () => void;
}

export function FirstSteps({ steps, onAsk, onOpenPage, onChanged }: Props) {
  const crm = useCrm();
  const ws = useWorkspace();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const now = nextStep(steps);
  const done = steps.filter((s) => s.done).length;
  const name = ws.tenant?.name;

  const move = (m: FirstStepMove) => {
    if (m.kind === "ask") onAsk(m.text);
    else if (m.kind === "page") onOpenPage(m.path, m.title);
    else {
      skipContacts(crm.tenantId);
      onChanged();
    }
  };
  const setSector = async (key: (typeof SECTOR_ANSWERS)[number]["key"]) => {
    setBusy(true);
    setError(null);
    try {
      await crm.applySectorDefaults(key);
      await ws.refresh();
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="first-steps" aria-labelledby="first-steps-h">
      <h3 id="first-steps-h">{done === 0 ? `Welcome${name ? `, ${name}` : ""}. Let's set you up.` : "Your first steps"}</h3>
      <p className="muted small">{done} of {steps.length} done. Ogma takes you through each one here; nothing is sent until you approve it.</p>
      <ol className="first-steps-list">
        {steps.map((s, i) => (
          <li key={s.key} className={s.done ? "is-done" : s === now ? "is-now" : ""} aria-current={s === now ? "step" : undefined}>
            <span className="first-steps-mark" aria-hidden="true">{s.done ? <Check size={14} /> : i + 1}</span>
            <span>
              <strong>{s.label}</strong>
              {s.done && <span className="sr-only"> (done)</span>}
              {s === now && (
                <>
                  <span className="muted small"> {s.hint}</span>
                  {/* The sector is a choice, drawn as Ogma's tap answers are; the other steps' moves stay buttons */}
                  <span className={s.key === "sector" ? "chat-answers" : "chat-confirm-buttons"} role="group" aria-label={s.label}>
                    {s.key === "sector"
                      ? SECTOR_ANSWERS.map((a) => (
                          <button key={a.key} type="button" className="chat-answer" disabled={busy} onClick={() => void setSector(a.key)}>
                            <span>{a.label}</span>
                            <ChevronRight size={16} aria-hidden="true" focusable="false" />
                          </button>
                        ))
                      : s.moves.map((m) => (
                          <button key={m.label} type="button" className={`btn btn-small ${m.kind === "skip" ? "btn-quiet" : "btn-primary"}`} onClick={() => move(m)}>
                            {m.label}
                          </button>
                        ))}
                  </span>
                </>
              )}
            </span>
          </li>
        ))}
      </ol>
      <Notice text={error} tone="bad" />
    </section>
  );
}
